// context/ResourceContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTrust } from './TrustContext';

const ResourceContext = createContext({
  userResources: null,
  resourceCategories: [],
  loading: false,
  error: null,
  registerResource: () => {},
  updateResourceAvailability: () => {},
  getResourcesByCategory: () => {},
  getUserResourcesForJob: () => {},
  getResourceVerificationStatus: () => {},
  refreshResources: () => {},
});

export const useResources = () => useContext(ResourceContext);

export const ResourceProvider = ({ children }) => {
  const { user } = useAuth();
  const { updateTrustScore } = useTrust();
  
  const [userResources, setUserResources] = useState({
    vehicles: [],
    equipment: [],
    spaces: []
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Predefined resource categories and types
  const resourceCategories = {
    vehicles: {
      name: 'Vehicles',
      icon: 'truck',
      types: [
        {
          id: 'pickup_truck',
          name: 'Pickup Truck',
          requirements: ['Vehicle registration', 'Insurance', 'Driver\'s license'],
          capacityUnit: 'lbs',
          minCapacity: 500
        },
        {
          id: 'cargo_van',
          name: 'Cargo Van',
          requirements: ['Vehicle registration', 'Insurance', 'Commercial license'],
          capacityUnit: 'cubic ft',
          minCapacity: 100
        },
        {
          id: 'box_truck',
          name: 'Box Truck',
          requirements: ['CDL', 'DOT registration', 'Commercial insurance'],
          capacityUnit: 'lbs',
          minCapacity: 5000
        },
        {
          id: 'refrigerated_vehicle',
          name: 'Refrigerated Vehicle',
          requirements: ['Vehicle registration', 'Food transport permit', 'Temperature logs'],
          capacityUnit: 'cubic ft',
          minCapacity: 50
        }
      ]
    },
    equipment: {
      name: 'Equipment',
      icon: 'tool',
      types: [
        {
          id: 'dolly',
          name: 'Hand Truck/Dolly',
          requirements: ['Photo of equipment'],
          capacityUnit: 'lbs',
          minCapacity: 200
        },
        {
          id: 'pallet_jack',
          name: 'Pallet Jack',
          requirements: ['Photo of equipment', 'Safety certification'],
          capacityUnit: 'lbs',
          minCapacity: 3000
        },
        {
          id: 'forklift',
          name: 'Forklift',
          requirements: ['Forklift certification', 'Equipment registration'],
          capacityUnit: 'lbs',
          minCapacity: 5000
        },
        {
          id: 'coolers',
          name: 'Portable Coolers',
          requirements: ['Photo of coolers', 'Capacity details'],
          capacityUnit: 'gallons',
          minCapacity: 20
        },
        {
          id: 'scales',
          name: 'Commercial Scales',
          requirements: ['Calibration certificate', 'Photo'],
          capacityUnit: 'lbs',
          minCapacity: 50
        }
      ]
    },
    spaces: {
      name: 'Spaces',
      icon: 'building',
      types: [
        {
          id: 'garage',
          name: 'Garage/Storage Space',
          requirements: ['Address', 'Photos', 'Square footage'],
          capacityUnit: 'sq ft',
          minCapacity: 100
        },
        {
          id: 'warehouse',
          name: 'Warehouse Space',
          requirements: ['Lease/ownership proof', 'Insurance', 'Loading dock details'],
          capacityUnit: 'sq ft',
          minCapacity: 1000
        },
        {
          id: 'cold_storage',
          name: 'Cold Storage',
          requirements: ['Health permits', 'Temperature logs', 'Capacity'],
          capacityUnit: 'cubic ft',
          minCapacity: 50
        },
        {
          id: 'commercial_kitchen',
          name: 'Commercial Kitchen',
          requirements: ['Health permit', 'Kitchen license', 'Equipment list'],
          capacityUnit: 'meals/day',
          minCapacity: 50
        },
        {
          id: 'event_space',
          name: 'Event Space',
          requirements: ['Occupancy permit', 'Insurance', 'Amenities list'],
          capacityUnit: 'people',
          minCapacity: 20
        }
      ]
    }
  };

  // Load user resources on mount
  useEffect(() => {
    if (user?.id) {
      loadUserResources();
    }
  }, [user]);

  const loadUserResources = () => {
    // Load from localStorage for demo
    const storedResources = localStorage.getItem(`member_resources_${user.id}`);
    if (storedResources) {
      setUserResources(JSON.parse(storedResources));
    }
  };

  // Register a new resource
  const registerResource = async (category, resourceData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate trust requirement
      const userTrustScore = user?.trust?.score || 3.0;
      if (userTrustScore < 3.5) {
        throw new Error('You need a trust score of 3.5 or higher to register resources');
      }
      
      // Create new resource entry
      const newResource = {
        id: `resource_${Date.now()}`,
        userId: user.id,
        category,
        type: resourceData.type,
        typeName: resourceCategories[category].types.find(t => t.id === resourceData.type)?.name,
        details: {
          make: resourceData.make,
          model: resourceData.model,
          year: resourceData.year,
          capacity: resourceData.capacity,
          capacityUnit: resourceData.capacityUnit,
          features: resourceData.features || [],
          condition: resourceData.condition,
          description: resourceData.description,
          photos: resourceData.photos || []
        },
        availability: {
          weekends: resourceData.availability?.weekends || false,
          evenings: resourceData.availability?.evenings || false,
          schedule: resourceData.availability?.schedule || [],
          blackoutDates: resourceData.availability?.blackoutDates || []
        },
        verification: {
          status: 'pending',
          documents: resourceData.documents || [],
          submittedAt: new Date().toISOString(),
          verifiedAt: null
        },
        location: resourceData.location || user.location,
        addedDate: new Date().toISOString()
      };
      
      // For demo, auto-verify basic resources
      if (['dolly', 'garage', 'pickup_truck'].includes(resourceData.type) && userTrustScore >= 3.5) {
        newResource.verification.status = 'verified';
        newResource.verification.verifiedAt = new Date().toISOString();
      }
      
      // Update state
      setUserResources(prev => ({
        ...prev,
        [category]: [...(prev[category] || []), newResource]
      }));
      
      // Save to localStorage
      const updatedResources = {
        ...userResources,
        [category]: [...(userResources[category] || []), newResource]
      };
      localStorage.setItem(`member_resources_${user.id}`, JSON.stringify(updatedResources));
      
      // Update trust score for adding resources
      if (newResource.verification.status === 'verified') {
        await updateTrustScore(user.id, 'resourceRegistration', 0.05);
      }
      
      return newResource;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update resource availability
  const updateResourceAvailability = async (resourceId, availabilityData) => {
    setLoading(true);
    setError(null);
    
    try {
      let resourceFound = false;
      const updatedResources = { ...userResources };
      
      // Find and update the resource
      Object.keys(updatedResources).forEach(category => {
        const index = updatedResources[category].findIndex(r => r.id === resourceId);
        if (index !== -1) {
          resourceFound = true;
          updatedResources[category][index] = {
            ...updatedResources[category][index],
            availability: {
              ...updatedResources[category][index].availability,
              ...availabilityData
            }
          };
        }
      });
      
      if (!resourceFound) {
        throw new Error('Resource not found');
      }
      
      setUserResources(updatedResources);
      localStorage.setItem(`member_resources_${user.id}`, JSON.stringify(updatedResources));
      
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get resources by category
  const getResourcesByCategory = (category) => {
    return userResources[category] || [];
  };

  // Get user resources that match job requirements
  const getUserResourcesForJob = (jobResourceRequirements) => {
    if (!jobResourceRequirements) return { matching: [], missing: [] };
    
    const matching = [];
    const missing = [];
    
    Object.entries(jobResourceRequirements).forEach(([category, requirements]) => {
      if (!requirements || requirements.length === 0) return;
      
      requirements.forEach(req => {
        const userResourcesInCategory = userResources[category] || [];
        const matchingResource = userResourcesInCategory.find(resource => {
          // Check type match
          if (resource.type !== req.type) return false;
          
          // Check capacity if required
          if (req.minCapacity && resource.details.capacity < req.minCapacity) return false;
          
          // Check verification status
          if (resource.verification.status !== 'verified') return false;
          
          // Check availability
          // TODO: Add more sophisticated availability checking
          
          return true;
        });
        
        if (matchingResource) {
          matching.push({ requirement: req, resource: matchingResource });
        } else {
          missing.push(req);
        }
      });
    });
    
    return { matching, missing };
  };

  // Get verification status for a resource
  const getResourceVerificationStatus = (resourceId) => {
    let resource = null;
    
    Object.values(userResources).forEach(categoryResources => {
      const found = categoryResources.find(r => r.id === resourceId);
      if (found) resource = found;
    });
    
    return resource?.verification?.status || 'unverified';
  };

  // Refresh resources from backend
  const refreshResources = async () => {
    loadUserResources();
  };

  const value = {
    userResources,
    resourceCategories,
    loading,
    error,
    registerResource,
    updateResourceAvailability,
    getResourcesByCategory,
    getUserResourcesForJob,
    getResourceVerificationStatus,
    refreshResources
  };

  return (
    <ResourceContext.Provider value={value}>
      {children}
    </ResourceContext.Provider>
  );
};